// Frontend API validation utilities using Zod schemas
// Provides runtime validation to ensure API responses match expected types

import { z } from 'zod';
import type {
  NoliaStartResponse,
  NoliaStatusResponse,
  NoliaJobStatusResponse,
  NoliaStatusResponseMapped,
  NoliaStartResponseMapped,
} from '@/types/api-types';

// ===== ZOD SCHEMAS (Mirror backend exactly) =====

export const AuthTokensSchema = z.object({
  accessToken: z.string().min(1),
  idToken: z.string().min(1),
  refreshToken: z.string().min(1),
});

export const LoginResponseSchema = z.object({
  success: z.boolean(),
  tokens: AuthTokensSchema.optional(),
  requiresNewPassword: z.boolean().optional(),
  session: z.string().optional(),
  error: z.string().optional(),
});

export const UploadedFileSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  s3_key: z.string().min(1),
});

export const NoliaStartRequestSchema = z.object({
  jobId: z.string().min(1).optional(), // Optional - generated by backend if not provided
  uploaded_file: z.array(UploadedFileSchema).min(1),
  global_kb: z.string().min(1),
  procurement_kb: z.string().min(1),
  user_timezone: z.string().optional(),
});

export const NoliaStatusResponseSchema = z.object({
  status: z.string(),
  results: z.any().optional(),
  error: z.string().optional(),
  job_id: z.string().optional(),
  progress: z.number().optional(),
  completed_at: z.string().optional(),
  created_at: z.string().optional(),
});

export const NoliaStartResponseSchema = z.object({
  job_id: z.string().optional(),
  status: z.string().optional(),
  message: z.string().optional(),
}).passthrough();

export const NoliaJobEventSchema = z.object({
  timestamp: z.string(),
  message: z.string(),
});

export const NoliaJobStatusResponseSchema = z.object({
  jobId: z.string(),
  status: z.string(),
  results: z.any().optional(),
  events: z.array(NoliaJobEventSchema).optional(),
  startedAt: z.string().optional(),
  lastUpdated: z.string().optional(),
  error: z.string().optional(),
  userId: z.string().optional(),
  appName: z.string().optional(),
  appType: z.string().optional(),
  name: z.string().optional(),
  inputs: z.any().optional(),
}).passthrough();

export const PresignedUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  fields: z.record(z.string(), z.string()).optional().default({}),
  s3Key: z.string().min(1),
  metadata: z.object({
    tenant_id: z.string().optional(),
    uploader_id: z.string().optional(),
    kb_id: z.string().optional(),
    uploaded_at: z.string(),
    original_filename: z.string(),
    file_size: z.number(),
    content_type: z.string(),
  }),
});

export const FileExistsResponseSchema = z.object({
  exists: z.boolean(),
  s3Key: z.string(),
});

export const FileDeletionResponseSchema = z.object({
  deleted: z.boolean(),
  s3Key: z.string(),
});

export const BucketInfoResponseSchema = z.object({
  bucketName: z.string(),
  region: z.string(),
});

export const HealthCheckResponseSchema = z.object({
  status: z.literal('healthy'),
  timestamp: z.string(),
  service: z.string(),
});

export const FrontendConfigResponseSchema = z.object({
  ROLE_ARN: z.string(),
  REGION: z.string(),
  API_ENDPOINT: z.string(),
  USER_POOL_ID: z.string(),
  CLIENT_ID: z.string(),
  CLIENT_NAME: z.string(),
  OUTPUTS_BUCKET_NAME: z.string(),
  DATA_BUCKET: z.string(),
  PROVISION_Q_RESOURCES: z.boolean(),
  PREFERRED_KNOWLEDGE_BASE: z.string(),
  BEDROCK_KNOWLEDGE_BASE_ID: z.string(),
  Q_APPLICATION_ID: z.string(),
  Q_INDEX_ID: z.string(),
  Q_RETRIEVER_ID: z.string(),
  BEDROCK_ACCOUNT: z.string(),
  PIPEDREAM_RELAY_LAMBDA_ARN: z.string(),
  PIPEDREAM_INTEGRATIONS: z.any(),
  BRANDING_PROVIDER_ENABLED: z.boolean(),
  AGENTS: z.array(z.any()),
  BRANDING_API_BASE_URL: z.string(),
  BRANDING_ASSETS_BUCKET: z.string(),
  BRANDING_ASSETS_PREFIX: z.string(),
  GROUPS: z.any(),
}).passthrough();

export const SRPHasherResponseSchema = z.object({
  hash: z.string().min(1),
});

export const TokenValidationResponseSchema = z.object({
  valid: z.boolean(),
});

// ===== VALIDATION UTILITIES =====

export class ApiValidationError extends Error {
  constructor(
    public endpoint: string,
    public _validationErrors: unknown,
    message?: string,
  ) {
    super(message || `API response validation failed for ${endpoint}`);
    this.name = 'ApiValidationError';
  }
}

/**
 * Validates API response data against a Zod schema
 */
export function validateApiResponse<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  endpoint: string,
): T {
  try {
    return schema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      console.error(`[API Validation Error] ${endpoint}:`, {
        errors: error.errors,
        data: JSON.stringify(data, null, 2),
      });
      throw new ApiValidationError(endpoint, error.errors);
    }
    throw error;
  }
}

/**
 * Safe validation that returns validation result without throwing
 */
export function safeValidateApiResponse<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
): { success: boolean; data?: T; error?: string } {
  try {
    const validatedData = schema.parse(data);
    return { success: true, data: validatedData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return {
        success: false,
        error: `Validation failed: ${error.errors.map(e => e.message).join(', ')}`,
      };
    }
    return { success: false, error: 'Unknown validation error' };
  }
}

// ===== FIELD MAPPING UTILITIES =====

/**
 * Maps snake_case API response fields to camelCase for better JS conventions
 */
export function mapNoliaStatusResponse(response: NoliaStatusResponse): NoliaStatusResponseMapped {
  return {
    status: response.status,
    results: response.results,
    error: response.error,
    jobId: response.job_id,
    progress: response.progress,
    completedAt: response.completed_at,
    createdAt: response.created_at,
  };
}

/**
 * Maps snake_case API response fields to camelCase for Nolia start response
 */
export function mapNoliaStartResponse(response: NoliaStartResponse): NoliaStartResponseMapped {
  return {
    jobId: response.job_id,
    status: response.status,
    message: response.message,
    ...Object.fromEntries(
      Object.entries(response).filter(([key]) => !['job_id', 'status', 'message'].includes(key)),
    ),
  };
}

// ===== SPECIFIC VALIDATION FUNCTIONS =====

export const validateLoginResponse = (data: unknown) =>
  validateApiResponse(LoginResponseSchema, data, '/auth/login');

export const validateNoliaStartResponse = (data: unknown) =>
  validateApiResponse(NoliaStartResponseSchema, data, '/nolia/main');

export const validateNoliaStatusResponse = (data: unknown) =>
  validateApiResponse(NoliaStatusResponseSchema, data, '/nolia/status');

export const validateNoliaJobStatusResponse = (data: unknown): NoliaJobStatusResponse =>
  validateApiResponse(NoliaJobStatusResponseSchema, data, '/nolia/jobs/:jobId');

export const validatePresignedUrlResponse = (data: unknown) =>
  validateApiResponse(PresignedUrlResponseSchema, data, '/files/upload-url');

export const validateFileExistsResponse = (data: unknown) =>
  validateApiResponse(FileExistsResponseSchema, data, '/files/check');

export const validateFileDeletionResponse = (data: unknown) =>
  validateApiResponse(FileDeletionResponseSchema, data, '/files/delete');

export const validateBucketInfoResponse = (data: unknown) =>
  validateApiResponse(BucketInfoResponseSchema, data, '/files/bucket-info');

export const validateHealthCheckResponse = (data: unknown) =>
  validateApiResponse(HealthCheckResponseSchema, data, '/files/health');

export const validateConfigResponse = (data: unknown) =>
  validateApiResponse(FrontendConfigResponseSchema, data, 'env-config');

export const validateSRPHasherResponse = (data: unknown) =>
  validateApiResponse(SRPHasherResponseSchema, data, '/srp-hasher');

export const validateTokenValidationResponse = (data: unknown) =>
  validateApiResponse(TokenValidationResponseSchema, data, '/auth/validate');
